/**
 * FavouritesPage — list of all favourite/wishlist lists
 *
 * Route: /favourites
 *
 * Features:
 *   - Search bar ("Search list name or person name")
 *   - List rows: name, "New" badge, creator, date, chevron, delete
 *   - Grey shading for new/shared lists
 *   - Delete confirmation
 *
 * @module pages/FavouritesPage
 * @since 2026-05-25
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import { useFavourites } from '@contexts/FavouritesContext';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function FavouritesPage() {
  const { lists, deleteList } = useFavourites();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return lists;
    const q = searchQuery.toLowerCase();
    return lists.filter(l =>
      l.name.toLowerCase().includes(q) ||
      l.createdBy.toLowerCase().includes(q)
    );
  }, [lists, searchQuery]);

  function handleDelete(listId) {
    deleteList(listId);
    setDeleteConfirm(null);
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Breadcrumbs items={[
        { label: 'Home', href: '/' },
        { label: 'Favourites' },
      ]} />

      <h1 className="font-sandvik-headline text-2xl font-normal text-carbon-black mb-6">
        Favourite list
      </h1>

      {/* Search bar */}
      <div className="flex gap-2 mb-8">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search list name or person name"
          className="flex-1 px-4 py-2.5 border border-n3 text-sm font-sandvik-text
                     text-carbon-black placeholder:text-n4
                     focus:outline-none focus:border-electric-blue"
        />
        <button
          type="button"
          className="px-6 py-2.5 bg-electric-blue text-white text-sm font-sandvik-text
                     hover:bg-eb-3 transition-colors"
        >
          Search
        </button>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="text-center py-12">
          <span className="material-symbols-outlined text-4xl text-n3 block mb-3">bookmark_border</span>
          <p className="text-sm text-n5 font-sandvik-text">
            {searchQuery ? 'No lists match your search.' : 'You have no favourite lists yet.'}
          </p>
        </div>
      )}

      {/* List rows */}
      {filtered.length > 0 && (
        <div className="space-y-0">
          {filtered.map(list => (
            <div
              key={list.id}
              className={`flex items-center gap-4 px-5 py-4 border-b border-n2
                         transition-colors
                         ${list.isNew ? 'bg-n1' : 'bg-white hover:bg-n1'}`}
            >
              {/* Mail icon */}
              <span className="material-symbols-outlined text-xl text-n4 flex-shrink-0">
                mail_outline
              </span>

              {/* Name + creator + date */}
              <Link
                to={`/favourites/${list.id}`}
                className="flex-1 min-w-0"
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-sandvik-text font-medium text-carbon-black truncate">
                    {list.name}
                  </span>
                  {list.isNew && (
                    <span className="text-xs text-electric-blue font-sandvik-text">
                      New
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-6 text-xs text-n5 font-sandvik-text">
                  <span>Created by : {list.createdBy}</span>
                  <span>Created date : {formatDate(list.createdDate)}</span>
                </div>
              </Link>

              {/* Chevron link */}
              <Link
                to={`/favourites/${list.id}`}
                className="text-vibrant-orange hover:text-electric-blue transition-colors flex-shrink-0"
              >
                <span className="material-symbols-outlined text-xl">chevron_right</span>
              </Link>

              {/* Delete button */}
              <button
                type="button"
                onClick={() => setDeleteConfirm(list.id)}
                className="text-n4 hover:text-carbon-black transition-colors flex-shrink-0"
                title="Delete list"
              >
                <span className="material-symbols-outlined text-xl">delete_outline</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-carbon-black/40 z-50" onClick={() => setDeleteConfirm(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white p-6 shadow-xl max-w-sm w-full">
              <h3 className="font-sandvik-headline text-lg text-carbon-black mb-3">
                Delete favourite list?
              </h3>
              <p className="text-sm text-n5 font-sandvik-text mb-6">
                This action cannot be undone. All products in this list will be removed.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-5 py-2 border border-n3 text-sm font-sandvik-text text-carbon-black
                             hover:bg-n1 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-5 py-2 bg-red-600 text-white text-sm font-sandvik-text
                             hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
